import MuiButton, { ButtonProps } from "@mui/material/Button";
import { SxProps } from "@mui/material/styles";

import {
  COLORS_TO_BE_REVIEWED,
  FIGMA_COLORS,
} from "components-redesign/styles/colors";

//m3 buttons guideline:
//https://m3.material.io/components/buttons/guidelines#9ecffdb3-ef29-47e7-8d5d-f78b404fcafe
//rendered example:
//https://material-web.dev/components/button/
type TM3Variant = "elevated" | "filled" | "tonal" | "outlined" | "text" | "secondary";
interface StyledButtonProps extends Omit<ButtonProps, "variant"> {
  //So far only these types are present in designs,
  //add other types from TM3Variant when needed
  variant?: "filled" | "text" | "link" | "outlined" | "secondary";
  "data-qa"?: string;
}

const M3_VARIANT_TO_M2_MAP: Record<
  TM3Variant,
  "text" | "outlined" | "contained"
> = {
  elevated: "contained",
  filled: "contained",
  tonal: "contained",
  outlined: "outlined",
  text: "text",
  secondary: "contained",
};
const BASE_BUTTON_STYLE = {
  textTransform: "none",
  borderRadius: "999px",
  "&.MuiButton-sizeLarge": {
    padding: "10px 24px",
  },
  fontWeight: 400,
};
const M3_VARIANT_STYLE: Record<TM3Variant, SxProps> = {
  elevated: BASE_BUTTON_STYLE,
  filled: {
    ...BASE_BUTTON_STYLE,
    backgroundColor: FIGMA_COLORS.onSurfaceLight,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: COLORS_TO_BE_REVIEWED.neutral15,
      opacity: 0.92,
    },
    "&:focus": {
      opacity: 0.9,
    },
  },
  tonal: BASE_BUTTON_STYLE,
  outlined: {
    ...BASE_BUTTON_STYLE,
    border: `1px solid ${COLORS_TO_BE_REVIEWED.neutral90}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1rem",
    color: FIGMA_COLORS.surfaceContainerHighestMonochromeDT,
    "& .MuiButton-icon": {
      color: COLORS_TO_BE_REVIEWED.buttonOutlinedBorderGray,
    },
  },
  text: {
    ...BASE_BUTTON_STYLE,
    color: FIGMA_COLORS.primaryLight,
    "&:hover": {
      color: FIGMA_COLORS.primaryLight,
      backgroundColor: FIGMA_COLORS.surfaceDimLight,
    },
    "&:focus": {
      color: FIGMA_COLORS.primaryLight,
      backgroundColor: FIGMA_COLORS.surfaceDimLight,
    },
  },
  secondary: {
    ...BASE_BUTTON_STYLE,
    backgroundColor: FIGMA_COLORS.secondaryContainerLight,
    color: FIGMA_COLORS.onSecondaryContainer,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: FIGMA_COLORS.secondaryContainerLight,
      opacity: 0.9,
    },
    "&:focus": {
      backgroundColor: FIGMA_COLORS.secondaryContainerLight,
      opacity: 0.88,
    },
    "&:active": {
      backgroundColor: FIGMA_COLORS.secondaryContainerLight,
      opacity: 0.85,
    },
  },
};

function Button({
  variant = "filled",
  size = "large",
  "data-qa": dataQa,
  ...props
}: StyledButtonProps) {
  const sx = props.sx
    ? ({ ...M3_VARIANT_STYLE[variant], ...props.sx } as SxProps)
    : (M3_VARIANT_STYLE[variant] as SxProps);

  if (variant === "link") {
    return (
      <a
        href=""
        data-qa={dataQa}
        className={props.className}
        style={{
          color: "black",
          ...props.style,
        }}
        onClick={(e) => {
          e.preventDefault();
          props.onClick?.(e as any);
        }}>
        {props.children}
      </a>
    );
  }

  return (
    <>
      <MuiButton
        data-qa={dataQa}
        {...props}
        size={size}
        variant={M3_VARIANT_TO_M2_MAP[variant]}
        sx={sx}
      />
    </>
  );
}

export default Button;
